// Targeted Count Setup - Choose Specific Items or Locations
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ArrowLeft,
  ArrowRight,
  Info,
  Target,
  Calendar as CalendarIcon,
  Users,
  Filter,
  Clock,
  Play,
  Save,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { format } from "date-fns";
import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { toast } from "sonner";
import {
  saveStockTake,
  generateStockTakeId,
  generateStockTakeReference,
  SavedStockTake,
} from "@/lib/stock-take-storage";

interface StockTakesPartialSetupPageProps {
  pageParams?: Record<string, any>;
}

export function StockTakesPartialSetupPage({ pageParams = {} }: StockTakesPartialSetupPageProps) {
  const { navigateTo } = useNavigation();
  const prepopulatedItems = pageParams.prepopulatedItems || [];
  const [startOption, setStartOption] = useState<"now" | "scheduled">("now");
  const [formData, setFormData] = useState({
    description: `Partial Stock Take - ${format(new Date(), "MMM dd, yyyy")}`,
    date: new Date(),
    scheduledDate: new Date(),
    warehouse: "",
    notes: "",
    assignedTo: "",
  });

  const [filters, setFilters] = useState({
    category: "",
    supplier: "",
    minValue: "",
    maxValue: "",
    location: "",
    includeZeroQty: false,
  });

  // Prepare warehouse options for combobox
  const warehouseOptions: ComboboxOption[] = [
    { value: "all", label: "All Warehouses", description: "Count all warehouses" },
    ...mockWarehouses
      .filter((w) => w.active)
      .map((warehouse) => ({
        value: warehouse.code,
        label: warehouse.name,
        description: warehouse.code,
      })),
  ];

  // Prepare user options for combobox
  const userOptions: ComboboxOption[] = [
    { value: "john-smith", label: "John Smith", description: "Warehouse Manager" },
    { value: "jane-doe", label: "Jane Doe", description: "Inventory Specialist" },
    { value: "mike-chen", label: "Mike Chen", description: "Stock Controller" },
    { value: "sarah-johnson", label: "Sarah Johnson", description: "Operations Lead" },
  ];

  // Prepare category options for combobox
  const categoryOptions: ComboboxOption[] = [
    { value: "all", label: "All Categories", description: "Include all categories" },
    { value: "electronics", label: "Electronics", description: "Electronic items" },
    { value: "furniture", label: "Furniture", description: "Furniture items" },
    { value: "raw-materials", label: "Raw Materials", description: "Raw material stock" },
    { value: "finished-goods", label: "Finished Goods", description: "Finished products" },
  ];

  // Prepare supplier options for combobox
  const supplierOptions: ComboboxOption[] = [
    { value: "all", label: "All Suppliers", description: "Include all suppliers" },
    { value: "supplier-1", label: "Supplier A", description: "SUP-001" },
    { value: "supplier-2", label: "Supplier B", description: "SUP-002" },
    { value: "supplier-3", label: "Supplier C", description: "SUP-003" },
  ];

  const handleContinue = () => {
    // Store form data and navigate to count page or schedule
    const stockTakeData = {
      ...formData,
      filters,
      prepopulatedItems, // Include prepopulated items from Stock Items page
      type: 'Targeted Count',
      status: startOption === "now" ? "In Progress" : "Scheduled",
      scheduledDate: startOption === "scheduled" ? formData.scheduledDate : undefined,
    };
    console.log("Targeted count setup:", stockTakeData);
    
    if (startOption === "now") {
      // Navigate to targeted count page
      navigateTo("stock-takes/targeted/count", { setupData: stockTakeData });
    } else {
      // Schedule the stock take and return to list
      // TODO: Save scheduled stock take to backend
      navigateTo("stock-takes");
    }
  };

  const handleSaveAndExit = () => {
    // Validation - description is required
    if (!formData.description) {
      toast.error("Please enter a description before saving");
      return;
    }

    try {
      const stockTakeId = generateStockTakeId();
      const reference = generateStockTakeReference();
      
      const savedStockTake: SavedStockTake = {
        id: stockTakeId,
        reference,
        description: formData.description,
        type: 'Targeted Count',
        status: 'Draft',
        currentStep: 1,
        createdAt: new Date(),
        lastModified: new Date(),
        createdBy: 'Current User', // Would come from auth context
        setupData: {
          countDate: format(startOption === "now" ? formData.date : formData.scheduledDate, "yyyy-MM-dd"),
          warehouse: formData.warehouse,
          assignedTo: formData.assignedTo,
        },
      };
      
      saveStockTake(savedStockTake);
      toast.success("Stock take saved successfully! You can resume it later from the Stock Takes page.");
      navigateTo("stock-takes");
    } catch (error) {
      toast.error("Failed to save stock take. Please try again.");
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateTo("stock-takes-home")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Title Section */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
            <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Targeted Count</h1>
            <p className="text-muted-foreground">
              Count specific items, categories, or locations
            </p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-green-900 dark:text-green-100">
                {prepopulatedItems.length > 0 
                  ? `${prepopulatedItems.length} item${prepopulatedItems.length > 1 ? 's' : ''} pre-selected for counting`
                  : "Partial Stock Take gives you full control"}
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                {prepopulatedItems.length > 0
                  ? "Items selected from the Stock Items page will be included in this count. You can still adjust the details below before continuing."
                  : "Use filters to select exactly which items to count. Perfect for spot checks, investigating discrepancies, or counting specific categories."}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Start Option Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>When to Start</CardTitle>
          <CardDescription>
            Choose whether to start counting now or schedule for later
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={startOption} onValueChange={(value: any) => setStartOption(value)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Label
                htmlFor="start-now"
                className={`relative flex items-start space-x-3 rounded-lg border-2 p-4 cursor-pointer transition-all ${
                  startOption === "now"
                    ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                }`}
              >
                <RadioGroupItem value="now" id="start-now" className="mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Play className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="font-semibold">Start Now</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Begin counting immediately after setup
                  </p>
                </div>
              </Label>

              <Label
                htmlFor="start-scheduled"
                className={`relative flex items-start space-x-3 rounded-lg border-2 p-4 cursor-pointer transition-all ${
                  startOption === "scheduled"
                    ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                }`}
              >
                <RadioGroupItem value="scheduled" id="start-scheduled" className="mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="font-semibold">Schedule for Later</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Schedule this stock take for a future date
                  </p>
                </div>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Setup Form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Stock Take Details</CardTitle>
          <CardDescription>
            Basic information about this partial stock take
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-destructive">*</span>
            </Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={`Partial Stock Take - ${format(formData.date, "MMM dd, yyyy")}`}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{startOption === "now" ? "Count Date" : "Scheduled Date"}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(startOption === "now" ? formData.date : formData.scheduledDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startOption === "now" ? formData.date : formData.scheduledDate}
                    onSelect={(date) => {
                      if (date) {
                        if (startOption === "now") {
                          setFormData({ ...formData, date });
                        } else {
                          setFormData({ ...formData, scheduledDate: date });
                        }
                      }
                    }}
                    disabled={startOption === "scheduled" ? (date) => date < new Date() : undefined}
                  />
                </PopoverContent>
              </Popover>
              {startOption === "scheduled" && (
                <p className="text-xs text-muted-foreground">
                  Stock take will appear as "Scheduled" until this date
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="warehouse">Warehouse</Label>
              <Combobox
                options={warehouseOptions}
                value={formData.warehouse || "all"}
                onValueChange={(value) => setFormData({ ...formData, warehouse: value === "all" ? "" : value })}
                placeholder="Select or search warehouse"
                searchPlaceholder="Search warehouses..."
                emptyText="No warehouse found."
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assignedTo">
              <Users className="h-4 w-4 inline mr-1" />
              Assigned To
            </Label>
            <Combobox
              options={userOptions}
              value={formData.assignedTo}
              onValueChange={(value) => setFormData({ ...formData, assignedTo: value })}
              placeholder="Select or search user"
              searchPlaceholder="Search users..."
              emptyText="No user found."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any notes or special instructions for this stock take"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Item Selection Filters */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            <CardTitle>Item Selection Filters</CardTitle>
          </div>
          <CardDescription>
            Choose which items to include in this count
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Combobox
                options={categoryOptions}
                value={filters.category || "all"}
                onValueChange={(value) => setFilters({ ...filters, category: value === "all" ? "" : value })}
                placeholder="Select or search category"
                searchPlaceholder="Search categories..."
                emptyText="No category found."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier</Label>
              <Combobox
                options={supplierOptions}
                value={filters.supplier || "all"}
                onValueChange={(value) => setFilters({ ...filters, supplier: value === "all" ? "" : value })}
                placeholder="Select or search supplier"
                searchPlaceholder="Search suppliers..."
                emptyText="No supplier found."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="minValue">Minimum Value</Label>
              <Input
                id="minValue"
                type="number"
                value={filters.minValue}
                onChange={(e) => setFilters({ ...filters, minValue: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxValue">Maximum Value</Label>
              <Input
                id="maxValue"
                type="number"
                value={filters.maxValue}
                onChange={(e) => setFilters({ ...filters, maxValue: e.target.value })}
                placeholder="No limit"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              placeholder="e.g., A01-R01 or leave blank for all"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="includeZeroQty"
              checked={filters.includeZeroQty}
              onCheckedChange={(checked) =>
                setFilters({ ...filters, includeZeroQty: checked as boolean })
              }
            />
            <Label
              htmlFor="includeZeroQty"
              className="text-sm font-normal cursor-pointer"
            >
              Include items with zero quantity
            </Label>
          </div>

          <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
            <p>
              <strong>Estimated items:</strong> {prepopulatedItems.length > 0 
                ? `${prepopulatedItems.length} pre-selected item${prepopulatedItems.length > 1 ? 's' : ''} will be included in this count.`
                : "Based on your filters, approximately 45 items will be included in this count."}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={() => navigateTo("stock-takes-home")}
        >
          Cancel
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndExit}
            disabled={!formData.description}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            Save and Exit
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!formData.description}
            className="gap-2"
          >
            {startOption === "now" ? (
              <>
                Continue to Count
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              <>
                <Clock className="h-4 w-4" />
                Schedule Stock Take
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
